/**
 * Shared syntax configuration for Crown DSL
 * Used by Monaco editor, VS Code extension, and LSP server.
 * Sourced entirely from the live DSL_GRAMMAR and knownFunctionNames()
 * so any new ops / built-ins are automatically reflected everywhere.
 */

import { DSL_GRAMMAR } from './dsl-grammar.js';
import { knownFunctionNames } from './expression/known-functions.js';

/** One highlighting rule: a pattern, and the token class it paints. */
export interface LanguageToken {
  pattern: RegExp;
  tokenType: string;
  description: string;
}

/** Everything an editor needs to highlight one of Crown's languages, so highlighting is defined once rather than per editor. */
export interface SyntaxConfig {
  keywords: string[];
  dslFunctions: string[];
  builtInFunctions: string[];
  booleanLiterals: string[];
  specialVars: string[];
  tokens: LanguageToken[];
}

// ---------------------------------------------------------------------------
// Live lists derived from the actual schema-dsl implementations
// ---------------------------------------------------------------------------

/** Every field / object / dates op name from DSL_GRAMMAR */
const DSL_OP_NAMES: string[] = [
  ...DSL_GRAMMAR.fieldOperations.map((op) => op.name),
  ...DSL_GRAMMAR.objectOperations.map((op) => op.name),
  ...DSL_GRAMMAR.dateOperations.map((op) => op.name),
];

/**
 * Every built-in function exported from expression/functions.ts.
 *
 * Read on demand, not at module evaluation. The names come from
 * `@crown/dsl-compiler`, whose engine is WASM in a browser and therefore
 * unavailable until `init()` has been awaited. Building the list eagerly makes
 * importing this module - and so importing `@crown/dsl` at all - throw in every
 * browser bundle that never touches the editor.
 */
let builtinFnNames: string[] | undefined;
function builtInFunctionNames(): string[] {
  builtinFnNames ??= [...knownFunctionNames()];
  return builtinFnNames;
}

// ---------------------------------------------------------------------------
// Crown DSL syntax configuration
// ---------------------------------------------------------------------------
export const crownSyntaxConfig: SyntaxConfig = {
  /** Control-flow / structural keywords for both expression and DSL layers */
  keywords: [
    // DSL root-level entry points
    'field',
    'object',
    'dates',
    // Expression control flow
    'match',
    'let',
    'return',
    'typeof',
    'if',
    'else',
    'then',
    // Logical connectives (natural language + expression)
    'and',
    'or',
    'not',
    // Membership operators
    'in',
    'not in',
    // DSL/natural language connector
    'type',
    // match wildcard
    '_',
  ],

  /** All DSL operation names (sourced from live DSL_GRAMMAR) */
  dslFunctions: DSL_OP_NAMES,

  /** All built-in expression functions (sourced from knownFunctionNames()) */
  get builtInFunctions(): string[] {
    return builtInFunctionNames();
  },

  booleanLiterals: ['true', 'false', 'null', 'undefined'],

  /** Special top-level identifiers available in every expression */
  specialVars: ['root', 'value', 'ctx'],

  tokens: [
    {
      pattern: /#.*$/,
      tokenType: 'comment',
      description: 'Single-line comment starting with #',
    },
    {
      pattern:
        /\b\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d{3})?(?:Z|[+-]\d{2}:\d{2})?)?\b/,
      tokenType: 'date',
      description: 'ISO date literal (YYYY-MM-DD or full ISO 8601)',
    },
    {
      pattern: /[£$€¥₹]\d+(?:\.\d+)?/,
      tokenType: 'currency',
      description: 'Currency literal (£19.99, $100.50, etc.)',
    },
    {
      pattern: /\d+\.\d+/,
      tokenType: 'number.float',
      description: 'Floating point number',
    },
    {
      pattern: /\d+/,
      tokenType: 'number',
      description: 'Integer number',
    },
    {
      pattern: /"([^"\\]|\\.)*"/,
      tokenType: 'string',
      description: 'Double-quoted string',
    },
    {
      pattern: /'([^'\\]|\\.)*'/,
      tokenType: 'string',
      description: 'Single-quoted string',
    },
    {
      pattern: /\|>/,
      tokenType: 'operator.pipeline',
      description: 'Pipeline operator |>',
    },
    {
      pattern: /=>/,
      tokenType: 'operator.arrow',
      description: 'Arrow operator =>',
    },
    {
      pattern: /\?\./,
      tokenType: 'operator',
      description: 'Optional chaining operator ?.',
    },
    {
      pattern: /[+\-*/%<>=!&|]/,
      tokenType: 'operator',
      description: 'Operators',
    },
    {
      pattern: /[?:]/,
      tokenType: 'operator',
      description: 'Ternary operators',
    },
    {
      pattern: /\./,
      tokenType: 'delimiter',
      description: 'Property access dot',
    },
    {
      pattern: /[a-zA-Z_]\w*/,
      tokenType: 'identifier',
      description: 'Identifier',
    },
  ],
};

/** A Monaco tokenizer rule: a pattern and the token class it produces. */
type MonacoTokenRule = [RegExp, string];

// ---------------------------------------------------------------------------
// Monaco token provider
// ---------------------------------------------------------------------------
export function getMonacoTokenProvider() {
  const {
    keywords,
    dslFunctions,
    builtInFunctions,
    booleanLiterals,
    specialVars,
  } = crownSyntaxConfig;

  return {
    tokenizer: {
      root: [
        [/#.*$/, 'comment'],
        [new RegExp(`\\b(${specialVars.join('|')})\\b`), 'variable.predefined'],
        [
          new RegExp(
            `\\b(${keywords.filter((k) => !k.includes(' ')).join('|')})\\b`,
          ),
          'keyword',
        ],
        [
          new RegExp(`\\b(${dslFunctions.join('|')})\\b`),
          'support.function.dsl',
        ],
        [
          new RegExp(`\\b(${builtInFunctions.join('|')})\\b`),
          'support.function.builtin',
        ],
        [
          new RegExp(`\\b(${booleanLiterals.join('|')})\\b`),
          'constant.language',
        ],
        [
          /\b\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d{3})?(?:Z|[+-]\d{2}:\d{2})?)?\b/,
          'number.date',
        ],
        [/[£$€¥₹]\d+(?:\.\d+)?/, 'number.currency'],
        [/\d+\.\d+/, 'number.float'],
        [/\d+/, 'number'],
        [/"([^"\\]|\\.)*"/, 'string'],
        [/'([^'\\]|\\.)*'/, 'string'],
        [/\|>/, 'keyword.operator'],
        [/=>/, 'keyword.operator'],
        [/\?\./, 'operator'],
        [/[+\-*/%<>=!&|]/, 'operator'],
        [/[?:]/, 'operator'],
        [/\./, 'delimiter'],
        [/[a-zA-Z_]\w*/, 'identifier'],
      ] satisfies MonacoTokenRule[],
    },
  };
}

// ---------------------------------------------------------------------------
// TextMate grammar patterns (used by generate-grammar.ts and VS Code extension)
// ---------------------------------------------------------------------------
export function getTextMatePatterns() {
  const {
    keywords,
    dslFunctions,
    builtInFunctions,
    booleanLiterals,
    specialVars,
  } = crownSyntaxConfig;

  // Escape regex-special characters in op names for use in patterns
  const escapedDsl = dslFunctions.map((s) =>
    s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
  );
  const escapedFns = builtInFunctions.map((s) =>
    s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
  );
  // Simple keywords (no spaces)
  const simpleKw = keywords.filter((k) => !k.includes(' '));

  return {
    comment: {
      name: 'comment.line.number-sign.crown',
      match: '#.*$',
    },
    specialVars: {
      name: 'support.variable.crown',
      match: `\\b(${specialVars.join('|')})\\b`,
    },
    keywords: {
      name: 'keyword.control.crown',
      match: `\\b(${simpleKw.join('|')})\\b`,
    },
    dslFunctions: {
      name: 'support.function.dsl.crown',
      match: `\\b(${escapedDsl.join('|')})\\b`,
    },
    builtInFunctions: {
      name: 'support.function.builtin.crown',
      match: `\\b(${escapedFns.join('|')})\\b`,
    },
    booleanLiterals: {
      name: 'constant.language.crown',
      match: `\\b(${booleanLiterals.join('|')})\\b`,
    },
    pipeline: {
      name: 'keyword.operator.pipeline.crown',
      match: '\\|>',
    },
    arrow: {
      name: 'keyword.operator.arrow.crown',
      match: '=>',
    },
    optionalChain: {
      name: 'keyword.operator.optional-chain.crown',
      match: '\\?\\.',
    },
    dateISO: {
      name: 'constant.numeric.date.crown',
      match:
        '\\b\\d{4}-\\d{2}-\\d{2}(?:T\\d{2}:\\d{2}:\\d{2}(?:\\.\\d{3})?(?:Z|[+-]\\d{2}:\\d{2})?)?\\b',
    },
    currency: {
      name: 'constant.numeric.currency.crown',
      match: '[£$€¥₹]\\d+(?:\\.\\d+)?',
    },
    numberFloat: {
      name: 'constant.numeric.float.crown',
      match: '\\b\\d+\\.\\d+\\b',
    },
    number: {
      name: 'constant.numeric.integer.crown',
      match: '\\b\\d+\\b',
    },
    stringDouble: {
      name: 'string.quoted.double.crown',
      begin: '"',
      end: '"',
      patterns: [{ name: 'constant.character.escape.crown', match: '\\\\.' }],
    },
    stringSingle: {
      name: 'string.quoted.single.crown',
      begin: "'",
      end: "'",
      patterns: [{ name: 'constant.character.escape.crown', match: '\\\\.' }],
    },
    operator: {
      name: 'keyword.operator.crown',
      match: '[+\\-*/%<>=!&|?:]',
    },
  };
}

// ---------------------------------------------------------------------------
// Language configuration for editors (brackets, comments, etc.)
// ---------------------------------------------------------------------------
export function getLanguageConfiguration() {
  return {
    comments: { lineComment: '#' },
    brackets: [
      ['{', '}'],
      ['[', ']'],
      ['(', ')'],
    ],
    autoClosingPairs: [
      { open: '{', close: '}' },
      { open: '[', close: ']' },
      { open: '(', close: ')' },
      { open: '"', close: '"' },
      { open: "'", close: "'" },
    ],
    surroundingPairs: [
      { open: '{', close: '}' },
      { open: '[', close: ']' },
      { open: '(', close: ')' },
      { open: '"', close: '"' },
      { open: "'", close: "'" },
    ],
  };
}
